import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { capitalize } from '@core/util/string';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	MatterClientType,
	MatterFileType,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';
import { combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import {
	CreateClientMiniModalComponent,
	CreateClientMiniModalData,
} from '../../fields/client/create/modal/modal.component';
import { ClientCreationData } from '@models/clients';
import { getClientAssociationType } from '@core/util/client';
import { TestHooks } from './other-parties.hooks';

enum ClientKebabOptionTypes {
	GLOBALVIEW = 'globalView',
	REMOVE = 'remove',
}

interface KebabOption {
	name: string;
	value: ClientKebabOptionTypes;
}

const ClientKebabOptions: KebabOption[] = [
	{ name: 'View global profile', value: ClientKebabOptionTypes.GLOBALVIEW },
	{ name: 'Remove from matter', value: ClientKebabOptionTypes.REMOVE },
];

interface ClientsWithPills {
	client: PersonMatterClient | BusinessMatterClient;
	pills: string[];
}

@Component({
	selector: 'app-parties-other-parties',
	templateUrl: './other-parties.component.html',
	animations: [openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class PartiesOtherPartiesComponent implements OnInit {
	testHooks = TestHooks;
	public addButtonText$!: Observable<string>;
	public clientKebabOptions = ClientKebabOptions;
	public otherParty = false;
	public showClerk = true;
	public showCapacity$!: Observable<boolean>;
	public title$!: Observable<string>;
	public clientsWithPills$!: Observable<ClientsWithPills[]>;
	public modalTitle: string;

	@Select(MatterClients.get_otherParties) clients$!: Observable<
		(PersonMatterClient | BusinessMatterClient)[]
	>;

	@Select(SelectedMatter.matterFileType)
	matterFileType$: Observable<MatterFileType>;
	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatter.otherPartyLawFirm) lawFirm$!: Observable<LawFirm>;
	@Select(SelectedMatter.otherPartyFileNumber) fileNumber$: Observable<string>;
	@ViewSelectSnapshot(SelectedMatter.matterFileType)
	matterFileType: MatterFileType;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;
	constructor(
		private matterClientDispatch: MatterClientDispatch,
		private matterDispatch: MatterDispatch,
		private drawerService: DrawerService,
		private matDialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.title$ = this.matterFileType$.pipe(
			map((fileType) =>
				fileType === MatterFileType.PURCHASE ? 'Vendor' : 'Purchaser',
			),
			tap((title) => (this.modalTitle = `Create New ${title}`)),
		);

		this.addButtonText$ = this.title$.pipe(
			map((title) => `Add ${title} to Matter`),
		);

		this.showCapacity$ = combineLatest([
			this.clients$,
			this.matterFileType$,
		]).pipe(
			map(
				([clients, fileType]) =>
					!!(fileType === MatterFileType.SALE && clients?.length),
			),
		);

		this.clientsWithPills$ = this.clients$.pipe(
			map((clients) =>
				clients.map((client) => ({
					client: client,
					pills: this.getClientPills(
						client as
							| PersonMatterClientWithAssociations
							| BusinessMatterClientWithAssociations,
					),
				})),
			),
		);
	}

	trackClient(
		index: number,
		item:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	): string {
		return item.id;
	}

	_onLawFirmUpdate(value: LawFirm): void {
		// if (this.isLeapMatter) {
		// 	// We are doing this differently for LEAP because this component does not follow the same flow (for reasons I don't understand) as law-firm-side-form.component.
		// 	// We need to change this component to follow the same flow as law-firm-side-form.component
		// 	if (!value) {
		// 		this.matterDispatch.RemoveLawFirm(this.matterId);
		// 	} else {
		// 		if ((value as any).selectLawyer) {
		// 			delete (value as any).selectLawyer;
		// 			this.matterDispatch.SelectLawFirmEmployee(
		// 				this.matterId,
		// 				value.id,
		// 				(value as any).lawyer.id,
		// 				LawFirmEmployeeType.LAWYER,
		// 				value.leap.personId,
		// 			);
		// 		} else if ((value as any).selectClerk) {
		// 			delete (value as any).selectClerk;
		// 			this.matterDispatch.SelectLawFirmEmployee(
		// 				this.matterId,
		// 				value.id,
		// 				(value as any).clerk.id,
		// 				LawFirmEmployeeType.CLERK,
		// 				value.clerk.leap.personId,
		// 			);
		// 		} else if ((value as any).removeLawyer) {
		// 			delete (value as any).removeLawyer;
		// 			this.matterDispatch.RemoveLawFirmEmployee(
		// 				this.matterId,
		// 				LawFirmEmployeeType.LAWYER,
		// 			);
		// 		} else if ((value as any).removeClerk) {
		// 			delete (value as any).removeClerk;
		// 			this.matterDispatch.RemoveLawFirmEmployee(
		// 				this.matterId,
		// 				LawFirmEmployeeType.CLERK,
		// 			);
		// 		} else if (
		// 			(value as any).createLawFirm ||
		// 			(value as any).updateLawFirm
		// 		) {
		// 			delete (value as any).createLawFirm;
		// 			delete (value as any).updateLawFirm;
		// 			this.matterDispatch.CreateLawFirm(this.matterId, value);
		// 		} else if ((value as any).createOrUpdateLawyer) {
		// 			delete (value as any).createOrUpdateLawyer;
		// 			this.matterDispatch.CreateLawFirmEmployee(
		// 				this.matterId,
		// 				(value as any).lawyer,
		// 				value.id,
		// 			);
		// 		} else if ((value as any).createOrUpdateClerk) {
		// 			delete (value as any).createOrUpdateClerk;
		// 			this.matterDispatch.CreateLawFirmEmployee(
		// 				this.matterId,
		// 				(value as any).clerk,
		// 				value.id,
		// 			);
		// 		} else {
		// 			this.matterDispatch.SelectLawFirm(
		// 				this.matterId,
		// 				value.id,
		// 				value.leap,
		// 			);
		// 		}
		//
		// 		this.deleteExtraProperties(value);
		// 	}
		// } else {
		if (value) {
			this.deleteExtraProperties(value);
		}
		this.matterDispatch.Update(this.matterId, { otherPartyLawFirm: value });
		//}
	}

	deleteExtraProperties(value: any) {
		delete (value as any).selectLawyer;
		delete (value as any).removeLawyer;
		delete (value as any).selectClerk;
		delete (value as any).removeClerk;
		delete (value as any).createLawFirm;
		delete (value as any).updateLawFirm;
		delete (value as any).createOrUpdateLawyer;
		delete (value as any).createOrUpdateClerk;
	}

	onFileNumberUpdated(value: string): void {
		this.matterDispatch.SetOtherPartyFileNumber(this.matterId, value);
	}

	onClientKebabSelect(
		clientId: string,
		$event: DropdownResult<KebabOption>,
	): void {
		const clientType =
			this.matterFileType === MatterFileType.PURCHASE
				? MatterClientType.SELLER
				: this.matterFileType === MatterFileType.SALE
					? MatterClientType.BUYER
					: MatterClientType.BORROWER;

		switch ($event.result.value) {
			case ClientKebabOptionTypes.GLOBALVIEW:
				this.drawerService.openDrawer(GlobalProfileComponent, {
					title: 'Global Profile',
					data: { clientId: clientId },
				});
				break;
			case ClientKebabOptionTypes.REMOVE:
				this.matterClientDispatch.RemoveClient(clientId, clientType);
				break;
		}
	}

	getClientPills(
		client:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	): string[] {
		const pills: string[] = [];
		if (client?.primaryContact) {
			pills.push('PRIMARY CONTACT');
		}

		client?.clientTypes?.forEach((type) => {
			pills.push(capitalize(type));
		});

		return pills;
	}

	addClient(): void {
		this.matDialog.open(CreateClientMiniModalComponent, {
			width: '800px',
			maxHeight: '850px',
			data: {
				title: this.modalTitle,
				contactTypeLabel: 'Contact Type',
				callbackFn: (clientData: ClientCreationData) =>
					new Promise((resolve) => {
						this.createAndAddClient(clientData);
						setTimeout(() => {
							resolve();
						}, 1500);
					}),
			} as CreateClientMiniModalData,
		});
	}

	createAndAddClient(clientData: ClientCreationData): void {
		this.matterClientDispatch.CreateAndAddClientToMatter({
			client: clientData.client,
			matterClientRelationship: {
				...clientData.matterClientRelationship,
				clientTypes: [getClientAssociationType(false, this.matterFileType)],
				client: false,
			},
		});
	}
}
